import { useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Mail, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Dismissible banner that nudges the user to verify their email.
 * Shown on Layout (wraps all authenticated pages) when user.email_verified === false.
 * Dismissal is session-scoped (sessionStorage) so it comes back on every new session
 * until actually verified.
 */
export default function EmailVerifyBanner() {
  const { user } = useAuth();
  const [hidden, setHidden] = useState(() => {
    try { return sessionStorage.getItem('email_verify_banner_hidden') === '1'; } catch { return false; }
  });
  const [sending, setSending] = useState(false);

  if (!user || user.email_verified || hidden) return null;

  const dismiss = () => {
    try { sessionStorage.setItem('email_verify_banner_hidden', '1'); } catch { /* noop */ }
    setHidden(true);
  };

  const resend = async () => {
    setSending(true);
    try {
      const res = await fetch(`${API}/api/auth/resend-verification`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(
          data.status === 'already_verified'
            ? 'Your email is already verified.'
            : `Verification email sent to ${user.email}.`
        );
        if (data.status === 'already_verified') setHidden(true);
      } else {
        toast.error(data.detail || 'Could not send verification email.');
      }
    } catch {
      toast.error('Network error. Try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="email-verify-banner"
      className="flex items-center gap-3 px-4 py-2.5 border-b border-amber-500/30 bg-amber-500/10 text-sm"
    >
      <Mail className="w-4 h-4 text-amber-600 shrink-0" />
      <span className="flex-1 text-foreground/90">
        Please confirm your email <strong className="text-foreground">{user.email}</strong>{' '}
        to unlock all features.
      </span>
      <button
        onClick={resend}
        disabled={sending}
        className="px-3 py-1 rounded-md text-xs font-semibold text-amber-700 hover:bg-amber-500/20 disabled:opacity-50 transition-colors"
        data-testid="resend-verification-btn"
      >
        {sending ? 'Sending…' : 'Resend email'}
      </button>
      <button
        onClick={dismiss}
        className="p-1 rounded text-amber-700 hover:bg-amber-500/20 transition-colors"
        aria-label="Dismiss"
        data-testid="dismiss-verify-banner-btn"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
